import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, ActionMenu, Input } from '../components';
import { Layout } from '../components/Layout';
import { formSchemaService } from '../api';
import pdfTemplateService from '../api/services/pdfTemplateService';
import pdfReportService from '../api/services/pdfReportService';
import { StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { Search, FileText } from 'lucide-react';

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const FormInfoBanner = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  border-left: 4px solid ${props => props.theme.colors.tint};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const CountBadge = styled.span`
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
  padding: 2px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
`;

const SearchContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
  flex-wrap: wrap;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
`;

const TemplateCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: ${props => props.theme.colors.tint};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TemplateName = styled(ThemedText)`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.large};
`;

const Badge = styled.span`
  background-color: ${props => props.theme.colors.tint};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.tiny};
  font-weight: 600;
  text-transform: uppercase;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardBody = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Metadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const MetaItem = styled(ThemedText)`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const CardFooter = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.muted};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: ${props => props.theme.colors.muted};
`;

const ReportsSection = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
`;

const ReportsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ReportsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ReportsTableHead = styled.thead`
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ReportsTableTh = styled.th`
  text-align: left;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.theme.typography.weights.semiBold};
  color: ${props => props.theme.colors.muted};
`;

const ReportsTableTd = styled.td`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border}20;
`;

const ReportStatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.tiny};
  font-weight: 600;
  text-transform: capitalize;
  background-color: ${props => {
    switch (props.$status) {
      case 'Completed': return '#22c55e20';
      case 'Processing': return '#3b82f620';
      case 'Pending': return '#f59e0b20';
      case 'Failed': return '#ef444420';
      default: return props.theme.colors.border;
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'Completed': return '#16a34a';
      case 'Processing': return '#2563eb';
      case 'Pending': return '#d97706';
      case 'Failed': return '#dc2626';
      default: return props.theme.colors.muted;
    }
  }};
`;

const Modal = styled.div`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 500px;
  width: 90%;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalBody = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.theme.typography.weights.semiBold};
`;

export const PDFTemplateManagerPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { formId } = useParams();

  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    templateId: null,
    templateName: ''
  });
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    templateId: null,
    templateName: '',
    newName: ''
  });
  const [recentReports, setRecentReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return templates;
    const lower = searchTerm.toLowerCase();
    return templates.filter(t =>
      t.name?.toLowerCase().includes(lower) ||
      t.description?.toLowerCase().includes(lower)
    );
  }, [templates, searchTerm]);

  useEffect(() => {
    loadData();
  }, [formId]);


  const loadData = async () => {
    try {
      setLoading(true);

      // Load form schema
      const form = await formSchemaService.getFormSchemaById(formId);
      setFormData(form);

      // Load templates from API (not localStorage)
      const templatesData = await pdfTemplateService.getTemplatesByFormId(
        parseInt(formId),
        false // includeInactive = false
      );
      setTemplates(templatesData);

      // Load recent generated reports for this form
      try {
        setReportsLoading(true);
        const reportsResponse = await pdfReportService.getAllReports({
          formSchemaId: formId,
          page: 1,
          pageSize: 5,
          sortField: 'createdAt',
          sortDirection: 'desc'
        });
        const items = reportsResponse?.items || reportsResponse?.data?.items || [];
        setRecentReports(items);
      } catch (err) {
        console.error('Failed to load recent reports:', err);
      } finally {
        setReportsLoading(false);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    navigate(`/forms/${formId}/pdf-templates/designer`);
  };

  const handleEditTemplate = (templateId) => {
    navigate(`/forms/${formId}/pdf-templates/designer/${templateId}`);
  };

  const handleCardClick = (templateId) => {
    navigate(`/forms/${formId}/pdf-templates/designer/${templateId}`);
  };

  const handleTestTemplate = (templateId) => {
    navigate(`/forms/${formId}/pdf-templates/test/${templateId}`);
  };

  const handleImageReportConfig = (templateId) => {
    navigate(`/forms/${formId}/pdf-templates/${templateId}/image-report`);
  };

  const handleDuplicateClick = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setDuplicateModal({
      isOpen: true,
      templateId,
      templateName: template.name,
      newName: `${template.name} (Copy)`
    });
  };

  const handleConfirmDuplicate = async () => {
    const { templateId, newName } = duplicateModal;
    if (!newName || !newName.trim()) return;

    try {
      const duplicated = await pdfTemplateService.duplicateTemplate(
        templateId,
        newName.trim()
      );
      setTemplates([...templates, duplicated]);
      setDuplicateModal({ isOpen: false, templateId: null, templateName: '', newName: '' });
      toast.success('Template duplicated successfully');
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      toast.error(error.message || 'Failed to duplicate template');
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateModal({ isOpen: false, templateId: null, templateName: '', newName: '' });
  };

  const handleSetAsDefault = async (templateId) => {
    try {
      await pdfTemplateService.setAsDefault(templateId);
      // Reload templates to reflect new default status
      await loadData();
      toast.success('Template set as default');
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error(error.message || 'Failed to set as default');
    }
  };

  const handleDeleteClick = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setDeleteConfirm({
      isOpen: true,
      templateId,
      templateName: template.name
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await pdfTemplateService.deleteTemplate(deleteConfirm.templateId);
      setTemplates(templates.filter(t => t.id !== deleteConfirm.templateId));
      setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' });
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error(error.message || 'Failed to delete template');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' });
  };

  const handleBackToSubmissions = () => {
    navigate(`/forms/${formId}/submissions`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout currentPath={`/forms/${formId}/pdf-templates`}>
        <Helmet>
          <title>Report Templates - aQRate Portal</title>
        </Helmet>
        <PageContainer theme={theme}>
          <LoadingContainer theme={theme}>
            <ThemedText>Loading templates...</ThemedText>
          </LoadingContainer>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath={`/forms/${formId}/pdf-templates`}>
      <Helmet>
        <title>Report Templates - {formData?.title || 'Form'} - aQRate Portal</title>
      </Helmet>

      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <HeaderLeft>
            {formData && (
              <FormInfoBanner theme={theme}>
                <ThemedText type="subtitle">
                  {formData.icon && `${formData.icon} `}
                  {formData.title}
                </ThemedText>
                {formData.description && (
                  <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
                    {formData.description}
                  </ThemedText>
                )}
              </FormInfoBanner>
            )}
            <TitleRow theme={theme}>
              <ThemedText type="title">Report Templates</ThemedText>
              {templates.length > 0 && (
                <CountBadge theme={theme}>
                  {templates.length} {templates.length === 1 ? 'template' : 'templates'}
                </CountBadge>
              )}
            </TitleRow>
            <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.xs }}>
              Create and manage report templates for this form
            </ThemedText>
          </HeaderLeft>

          <HeaderRight theme={theme}>
            <Button variant="secondary" onClick={handleBackToSubmissions}>
              Back to Submissions
            </Button>
            <Button variant="primary" onClick={handleCreateTemplate}>
              + Create Template
            </Button>
          </HeaderRight>
        </PageHeader>

        {templates.length > 0 && (
          <SearchContainer theme={theme}>
            <StyledSearchContainer theme={theme}>
              <SearchIcon><Search size={16} /></SearchIcon>
              <TransparentInput
                placeholder="Search templates by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                theme={theme}
              />
            </StyledSearchContainer>
          </SearchContainer>
        )}

        {templates.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><FileText size={48} /></EmptyIcon>
            <ThemedText type="subtitle">No report templates yet</ThemedText>
            <ThemedText type="small">
              Create a template to generate reports from form submissions
            </ThemedText>
            <Button variant="primary" onClick={handleCreateTemplate}>
              Create First Template
            </Button>
          </EmptyState>
        ) : filteredTemplates.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><Search size={48} /></EmptyIcon>
            <ThemedText type="subtitle">No matching templates</ThemedText>
            <ThemedText type="small">
              No templates match "{searchTerm}". Try a different search term.
            </ThemedText>
          </EmptyState>
        ) : (
          <TemplateGrid theme={theme}>
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                theme={theme}
                onClick={() => handleCardClick(template.id)}
              >
                <CardHeader theme={theme}>
                  <TemplateName theme={theme}>{template.name}</TemplateName>
                  <ActionMenu actions={[
                    {
                      label: 'Duplicate',
                      onClick: () => handleDuplicateClick(template.id)
                    },
                    ...(!template.isDefault ? [{
                      label: 'Set as Default',
                      onClick: () => handleSetAsDefault(template.id)
                    }] : []),
                    {
                      label: 'Delete',
                      onClick: () => handleDeleteClick(template.id),
                      variant: 'danger'
                    }
                  ]} />
                </CardHeader>

                {template.description && (
                  <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.sm }}>
                    {template.description}
                  </ThemedText>
                )}

                {(template.hasImageReportConfig || template.isDefault) && (
                  <BadgeRow theme={theme}>
                    {template.hasImageReportConfig && (
                      <Badge theme={theme} style={{ backgroundColor: '#6f42c1' }}>Image Report</Badge>
                    )}
                    {template.isDefault && (
                      <Badge theme={theme}>Default</Badge>
                    )}
                  </BadgeRow>
                )}

                <CardBody>
                  <Metadata>
                    <MetaItem theme={theme}>
                      {template.createdByName ? `Created by ${template.createdByName}` : 'Created'}
                      {' - '}
                      {formatDate(template.createdAt)}
                    </MetaItem>
                    {template.updatedAt !== template.createdAt && (
                      <MetaItem theme={theme}>
                        Updated {formatDate(template.updatedAt)}
                      </MetaItem>
                    )}
                  </Metadata>
                </CardBody>

                <CardFooter theme={theme}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditTemplate(template.id);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestTemplate(template.id);
                    }}
                  >
                    Test
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageReportConfig(template.id);
                    }}
                  >
                    Image Report
                  </Button>
                </CardFooter>
              </TemplateCard>
            ))}
          </TemplateGrid>
        )}

        {/* Recent Generated Reports */}
        <ReportsSection theme={theme}>
          <ReportsSectionHeader theme={theme}>
            <ThemedText type="subtitle">Recent Generated Reports</ThemedText>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/generated-reports?formSchemaId=${formId}`)}
            >
              View All
            </Button>
          </ReportsSectionHeader>

          {reportsLoading ? (
            <ThemedText type="small" style={{ color: theme.colors.muted }}>Loading reports...</ThemedText>
          ) : recentReports.length === 0 ? (
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              No reports generated yet
            </ThemedText>
          ) : (
            <ReportsTable>
              <ReportsTableHead theme={theme}>
                <tr>
                  <ReportsTableTh theme={theme}>Status</ReportsTableTh>
                  <ReportsTableTh theme={theme}>Template</ReportsTableTh>
                  <ReportsTableTh theme={theme}>Source #</ReportsTableTh>
                  <ReportsTableTh theme={theme}>Created</ReportsTableTh>
                  <ReportsTableTh theme={theme}>Size</ReportsTableTh>
                  <ReportsTableTh theme={theme}>Actions</ReportsTableTh>
                </tr>
              </ReportsTableHead>
              <tbody>
                {recentReports.map(report => (
                  <tr key={report.id}>
                    <ReportsTableTd theme={theme}>
                      <ReportStatusBadge theme={theme} $status={report.status}>
                        {report.status}
                      </ReportStatusBadge>
                    </ReportsTableTd>
                    <ReportsTableTd theme={theme}>{report.pdfTemplateName || '-'}</ReportsTableTd>
                    <ReportsTableTd theme={theme}>#{report.formSubmissionId}</ReportsTableTd>
                    <ReportsTableTd theme={theme}>
                      {report.createdAt ? new Date(report.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    </ReportsTableTd>
                    <ReportsTableTd theme={theme}>
                      {report.fileSizeBytes ? (report.fileSizeBytes < 1024 * 1024 ? `${(report.fileSizeBytes / 1024).toFixed(0)} KB` : `${(report.fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`) : '-'}
                    </ReportsTableTd>
                    <ReportsTableTd theme={theme}>
                      {report.status === 'Completed' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={async () => {
                            try {
                              const result = await pdfReportService.getDownloadUrl(report.id);
                              const url = result?.downloadUrl || result?.url;
                              if (url) window.open(url, '_blank');
                            } catch {
                              toast.error('Failed to download report');
                            }
                          }}
                        >
                          Download
                        </Button>
                      )}
                    </ReportsTableTd>
                  </tr>
                ))}
              </tbody>
            </ReportsTable>
          )}
        </ReportsSection>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteConfirm.isOpen} theme={theme}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Confirm Delete</ThemedText>
            </ModalHeader>
            <ModalBody theme={theme}>
              <ThemedText>
                Are you sure you want to delete "{deleteConfirm.templateName}"?
              </ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm }}>
                This action cannot be undone.
              </ThemedText>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete}>
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Duplicate Template Modal */}
        <Modal isOpen={duplicateModal.isOpen} theme={theme}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Duplicate Template</ThemedText>
            </ModalHeader>
            <ModalBody theme={theme}>
              <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
                Create a copy of "{duplicateModal.templateName}"
              </ThemedText>
              <FormGroup theme={theme}>
                <Label theme={theme}>New Template Name</Label>
                <Input
                  placeholder="Enter template name"
                  value={duplicateModal.newName}
                  onChange={(e) => setDuplicateModal(prev => ({ ...prev, newName: e.target.value }))}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={handleCancelDuplicate}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDuplicate}
                disabled={!duplicateModal.newName.trim()}
              >
                Duplicate
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </PageContainer>
    </Layout>
  );
};
